import React, { useContext } from 'react';
import ModalModule from '../modal';
import Utils from '../utils';

const { Modal, ModalButtonActionType, ModalContext } = ModalModule;
const { Session, Util } = Utils;

interface PlanChangedSuccessfulModalData {
	onUpgradePlanCallback?: (planId: number) => void;
	newPlanId: number;
	newPlanPrice: number;
	isUpgrade: boolean;
}

interface PlanChangedSuccessfulModalProps {
	modalId: string;
	data: PlanChangedSuccessfulModalData;
}

const PlanChangedSuccessfulModal = ({
	modalId,
	data,
}: PlanChangedSuccessfulModalProps) => {
	const { onUpgradePlanCallback, newPlanId, newPlanPrice, isUpgrade } = data;
	const { closeModal } = useContext(ModalContext);
	const contactNumber = Util.getFraudNumber();
	const { BillingDayOfMonth } = Session.getInternalAccountInfo();
	const { CurrentServiceFees = '' } = Session.getAccountInfo();
	const billingDate = Util.getBillingDate(BillingDayOfMonth);

	const onPrimaryButtonClick = () => {
		if (onUpgradePlanCallback) {
			onUpgradePlanCallback(newPlanId);
		}
		closeModal(modalId, ModalButtonActionType.Ok);
	};

	const primaryButton = {
		type: ModalButtonActionType.Ok,
		text: 'OK',
		onClick: onPrimaryButtonClick,
		dataqa: 'plan-changed-successful-ok-button',
	};

	const renderFooter = () => (
		<div className="footer">
			<span className="message-icon" />
			<p className="footer-text">
				<a
					href="https://help.stamps.com/hc/en-us/articles/20821937508379"
					target="_blank"
					rel="noopener noreferrer"
				>
					Learn more about your subscriptions, plans, and billing cycles
				</a>{' '}
				, or contact support with your questions at {contactNumber},
				Monday-Friday 8 am - 8 pm Central Time.
			</p>
		</div>
	);

	const planChangeText = () => {
		if (isUpgrade) {
			return (
				<div className="plan-change-info">
					<p>
						Your new features will be ready to use in just a few minutes. Your
						invoice on {billingDate} will reflect a prorated amount for your new
						plan, based on the days left in your current cycle.
					</p>
					{renderFooter()}
				</div>
			);
		} else {
			const nextDayBillingDate = Util.getNextDay(billingDate);
			return (
				<div className="plan-change-info">
					<p className="section1">
						Your new plan will go into effect on the first day of your next
						billing cycle {nextDayBillingDate}.
					</p>
					<p className="section2">
						Here are a few things to keep in mind about your billing cycles:
					</p>
					<div className="list">
						<ul>
							<li>
								Stamps.com bills at the end of the billing cycle for the prior
								month of service
							</li>
							<li>
								Your next bill will reflect your current plan cost ($
								{CurrentServiceFees}) minus a prorated amount from your new plan
								selection.
							</li>
							<li>
								The following bills will reflect your new plan cost ($
								{newPlanPrice}) + applicable taxes, if any.
							</li>
						</ul>
					</div>
					{renderFooter()}
				</div>
			);
		}
	};

	return (
		<Modal
			modalId={modalId}
			className="subscription-plan plan-changed-succesful-modal"
			header="You've Unlocked New Features!"
			primaryButton={primaryButton}
		>
			<div className="body">{planChangeText()}</div>
		</Modal>
	);
};

export default PlanChangedSuccessfulModal;
